import { AssetModel, isPositionModelWithLatLng } from '@customer/store/apis/assetsApi';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocaleFormatter } from '@customer/shared/hooks/useLocaleFormatter';
import { LayerGroup } from 'react-leaflet';
import { DateTime } from 'luxon';
import AssetPositionMarker, {
  AssetPositionMarkerProps,
  AssetReportingType,
} from '../markers/asset-position-marker/AssetPositionMarker';
import { getRotationProps } from '../markers/utils';
import { isOutsideOfReportingGracePeriod, OLDEST_TIME } from '@customer/config/time';

export interface AssetPanelDetailsProps {
  asset: AssetModel;
}

const getReportingType = (lastReportTime: DateTime): AssetReportingType => {
  const lastReportDiff = lastReportTime.diffNow().as('days');

  if (lastReportDiff < -7) {
    return AssetReportingType.ReportedOver7DaysAgo;
  } else if (lastReportDiff < -1) {
    return AssetReportingType.ReportedOver1DayAgo;
  } else {
    return AssetReportingType.Reporting;
  }
};

export function SingleAssetPositionLayer({ asset }: AssetPanelDetailsProps) {
  const { t } = useTranslation();
  const { parseFromIso, showRelative } = useLocaleFormatter();

  const layerData = useMemo<AssetPositionMarkerProps[]>(() => {
    const layerData: AssetPositionMarkerProps[] = [];

    const minTime = DateTime.fromISO(OLDEST_TIME);
    const lastGoodReportTime = asset.posG ? parseFromIso(asset.posG.ts) : minTime;
    const lastSeenTime = asset.posS ? parseFromIso(asset.posS.ts) : minTime;

    if (isPositionModelWithLatLng(asset.posG)) {
      layerData.push({
        ...getRotationProps(asset.posG),
        reportingType: getReportingType(lastGoodReportTime),
        label: `${t('LastGoodReport')} ${showRelative(lastGoodReportTime)}`,
      });
    }

    // Only show "last seen" if it's newer than the last reported
    if (
      isPositionModelWithLatLng(asset.posS) &&
      isOutsideOfReportingGracePeriod(lastGoodReportTime, lastSeenTime)
    ) {
      layerData.push({
        ...getRotationProps(asset.posS),
        reportingType: AssetReportingType.LastSeen,
        label: `${t('LastSeen')} ${showRelative(lastSeenTime)}`,
      });
    }

    return layerData;
  }, [asset.posG, asset.posS, parseFromIso, showRelative, t]);

  return (
    <LayerGroup>
      <>
        {layerData.map((layerProps: AssetPositionMarkerProps, idx: number) => {
          return <AssetPositionMarker key={idx} {...layerProps} />;
        })}
      </>
    </LayerGroup>
  );
}

export default SingleAssetPositionLayer;
